import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AdminPage, AdminPageHeader } from "@/components/admin-ui";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  CreatorApplication,
  DmError,
  artistHubEnabled,
  artistOwnersGet,
  creatorApplicationApprove,
  creatorApplicationList,
  creatorApplicationReject,
  artistProfileGet,
} from "@/services/firebase/artistHub";
import { getActorPhotoUrl, getActors } from "@/services/firebase/actor";
import {
  CREATOR_REJECT_PRESETS,
  CREATOR_STATUS_LABELS,
  CREATOR_STATUS_ORDER,
  CreatorApplicationStatus,
  NEW_ARTIST_BIO_MAX,
  NEW_ARTIST_NAME_MAX,
  REJECT_REASON_MAX,
  buildApproveBody,
  creatorKindLabel,
  creatorLinkHref,
  creatorStatusVariant,
  otherOwners,
  ownersLabel,
  prefillBio,
  rejectReasonProblem,
} from "@/lib/creator";

const fmt = (ms: number | null | undefined) => (ms ? new Date(ms).toLocaleString("zh-TW", { hour12: false }) : "—");
const errorText = (e: unknown) => (e instanceof Error ? e.message : "操作未完成，請重試");
const isStateConflict = (e: unknown) => e instanceof DmError && e.code === "CREATOR_APPLICATION_STATE";
const isAlreadyOwned = (e: unknown) => e instanceof DmError && e.code === "ARTIST_ALREADY_OWNED";

/**
 * C-04: 創作者申請審核佇列（B-40, contract §1e v2.6; staff only）.
 *   creatorApplicationList {status, cursor, limit} → pending / approved / rejected tabs, 載入更多
 *   creatorApplicationApprove {uid, artistId} | {uid, newArtist} → server binds the artist member + users.creator, pushes creator_activated
 *   creatorApplicationReject {uid, reason}
 *   artistOwnersGet {artistId} (B-45) → 「目前擁有者」; binding to an artist with another owner needs the co-owner checkbox (force: true)
 * Design: docs/v3/工作單/ReelPlus-v3-創作者帳號與經營中心UIUX重新設計-20261016.md §2.2.
 */
export default function CreatorApplicationsPage() {
  const [status, setStatus] = useState<CreatorApplicationStatus>("pending");
  const [approving, setApproving] = useState<CreatorApplication | null>(null);
  const [rejecting, setRejecting] = useState<CreatorApplication | null>(null);
  const [notice, setNotice] = useState("");
  const list = useInfiniteQuery({
    queryKey: ["creator-applications", status],
    queryFn: ({ pageParam }) => creatorApplicationList({ status, cursor: pageParam }),
    initialPageParam: null as string | null,
    getNextPageParam: (last) => last.nextCursor ?? undefined,
    enabled: artistHubEnabled,
    retry: false,
  });

  if (!artistHubEnabled) {
    return (
      <AdminPage>
        <AdminPageHeader title="創作者申請" />
        <p>此環境尚未開放藝人專區（創作者申請）。</p>
      </AdminPage>
    );
  }
  const items = list.data?.pages.flatMap((p) => p.applications) ?? [];
  const notDeployed = list.error instanceof DmError && list.error.status === 404 && !list.error.code;
  return (
    <AdminPage maxWidth="wide">
      <AdminPageHeader
        title="創作者申請"
        subtitle="App「成為創作者」送出的申請；核准會綁定或建立藝人專頁並開通創作者帳號"
        loading={list.isFetching}
        onRefresh={() => void list.refetch()}
      />
      <Tabs
        value={status}
        onValueChange={(v) => {
          setStatus(v as CreatorApplicationStatus);
          setNotice("");
        }}
        className="mb-4"
      >
        <TabsList>
          {CREATOR_STATUS_ORDER.map((s) => (
            <TabsTrigger key={s} value={s}>
              {CREATOR_STATUS_LABELS[s]}
            </TabsTrigger>
          ))}
        </TabsList>
      </Tabs>
      {notice && (
        <p role="status" className="mb-4 text-sm text-success">
          {notice}
        </p>
      )}
      {list.isError &&
        (notDeployed ? (
          <p role="status" className="text-sm text-muted-foreground">
            後端尚未提供 creatorApplicationList（B-40），部署後即可使用。
          </p>
        ) : (
          <p role="alert" className="text-destructive">
            {errorText(list.error)}
          </p>
        ))}
      {list.isPending && <p>讀取申請…</p>}
      {list.isSuccess && items.length === 0 && (
        <p className="text-sm text-muted-foreground">{status === "pending" ? "目前沒有待審核的申請。" : `沒有${CREATOR_STATUS_LABELS[status]}的申請。`}</p>
      )}
      <ul className="space-y-3">
        {items.map((a) => (
          <li key={a.uid}>
            <ApplicationCard
              application={a}
              onApprove={() => {
                setNotice("");
                setApproving(a);
              }}
              onReject={() => {
                setNotice("");
                setRejecting(a);
              }}
            />
          </li>
        ))}
      </ul>
      {list.hasNextPage && (
        <Button className="mt-4" variant="outline" disabled={list.isFetchingNextPage} onClick={() => void list.fetchNextPage()}>
          {list.isFetchingNextPage ? "載入中…" : "載入更多"}
        </Button>
      )}
      {approving && (
        <ApproveDialog
          application={approving}
          onClose={() => setApproving(null)}
          onDone={(artistId) => {
            setApproving(null);
            setNotice(`已開通，App 會收到推播（藝人 ID：${artistId}）`);
          }}
        />
      )}
      {rejecting && (
        <RejectDialog
          application={rejecting}
          onClose={() => setRejecting(null)}
          onDone={() => {
            setRejecting(null);
            setNotice(`已拒絕「${rejecting.displayName}」的申請`);
          }}
        />
      )}
    </AdminPage>
  );
}

function ApplicationCard({ application: a, onApprove, onReject }: { application: CreatorApplication; onApprove: () => void; onReject: () => void }) {
  const initial = [...(a.displayName || a.email || a.uid || "?")][0];
  return (
    <Card>
      <CardContent className="flex flex-col gap-3 p-4 sm:flex-row">
        {a.photoURL ? (
          <img src={a.photoURL} alt="" className="h-12 w-12 shrink-0 rounded-full object-cover" />
        ) : (
          <div aria-hidden className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-muted text-lg">
            {initial}
          </div>
        )}
        <div className="min-w-0 flex-1 space-y-1">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-base font-semibold break-words">{a.displayName || "（未填藝名）"}</span>
            <Badge variant="outline">{creatorKindLabel(a.kind)}</Badge>
            <Badge variant={creatorStatusVariant(a.status)}>{CREATOR_STATUS_LABELS[a.status] ?? a.status}</Badge>
          </div>
          <p className="text-sm text-muted-foreground break-all">
            {a.email || "（無 Email）"} · <span className="font-mono text-xs">{a.uid}</span>
          </p>
          {a.links.length > 0 && (
            <ul className="flex flex-wrap gap-x-3 gap-y-1 text-sm">
              {a.links.map((link, i) => {
                const href = creatorLinkHref(link);
                return (
                  <li key={`${i}-${link}`} className="max-w-full break-all">
                    {href ? (
                      <a href={href} target="_blank" rel="noopener noreferrer" className="text-primary underline underline-offset-2">
                        {link}
                      </a>
                    ) : (
                      link
                    )}
                  </li>
                );
              })}
            </ul>
          )}
          {a.bio && <p className="whitespace-pre-wrap text-sm break-words">{a.bio}</p>}
          <p className="text-xs text-muted-foreground">送出時間：{fmt(a.createdAt)}</p>
          {a.status !== "pending" && (
            <div className="space-y-1 rounded border p-2 text-sm">
              <p>
                審核：<span className="font-mono text-xs break-all">{a.reviewedBy || "—"}</span> · {fmt(a.reviewedAt ?? a.updatedAt)}
              </p>
              {a.status === "approved" && a.artistId && (
                <p>
                  藝人：
                  <Link to={`/actors/${encodeURIComponent(a.artistId)}/edit`} className="font-mono text-xs text-primary underline underline-offset-2">
                    {a.artistId}
                  </Link>
                </p>
              )}
              {a.status === "rejected" && a.reason && <p className="break-words">原因：{a.reason}</p>}
            </div>
          )}
        </div>
        {a.status === "pending" && (
          <div className="flex shrink-0 gap-2 sm:flex-col">
            <Button onClick={onApprove}>核准</Button>
            <Button variant="outline" onClick={onReject}>
              拒絕
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function useApplicationMutationDone() {
  const client = useQueryClient();
  return () => void client.invalidateQueries({ queryKey: ["creator-applications"] });
}

function ApproveDialog({ application, onClose, onDone }: { application: CreatorApplication; onClose: () => void; onDone: (artistId: string) => void }) {
  const refresh = useApplicationMutationDone();
  const [mode, setMode] = useState<"existing" | "new">("existing");
  const [search, setSearch] = useState("");
  const [artistId, setArtistId] = useState("");
  const [displayName, setDisplayName] = useState(application.displayName ?? "");
  const [bio, setBio] = useState(prefillBio(application.bio));
  const [formError, setFormError] = useState("");
  // B-45: binding to an artist that already has another owner needs an explicit co-owner confirmation (force: true).
  const [coOwnerConfirmed, setCoOwnerConfirmed] = useState(false);
  const actors = useQuery({ queryKey: ["actors"], queryFn: () => getActors(), enabled: mode === "existing" });
  // 公開專頁頭像（artist-public 複本，一定可讀）；actors.photoURL 在部分環境不公開，只當備援。
  const profile = useQuery({
    queryKey: ["artist-profile", artistId],
    queryFn: () => artistProfileGet(artistId),
    enabled: mode === "existing" && !!artistId,
    retry: false,
  });
  const owners = useQuery({
    queryKey: ["artist-owners", artistId],
    queryFn: () => artistOwnersGet(artistId),
    enabled: mode === "existing" && !!artistId,
    retry: false,
  });
  const others = otherOwners(owners.data?.owners, application.uid);
  const needsCoOwnerConfirm = mode === "existing" && !!artistId && others.length > 0;
  const matches = useMemo(() => {
    const q = search.trim().toLowerCase();
    const all = actors.data ?? [];
    return (q ? all.filter((a) => a.name.toLowerCase().includes(q) || a.id.toLowerCase().includes(q)) : all).slice(0, 30);
  }, [actors.data, search]);
  const selected = actors.data?.find((a) => a.id === artistId) ?? null;
  const approve = useMutation({
    mutationFn: () =>
      creatorApplicationApprove(
        buildApproveBody(
          application.uid,
          mode === "existing" ? { mode, artistId, force: needsCoOwnerConfirm && coOwnerConfirmed } : { mode, displayName, bio },
        ),
      ),
    onSuccess: (r) => {
      refresh();
      onDone(r.artistId);
    },
    onError: (e) => {
      if (isStateConflict(e)) refresh();
      // Someone became an owner after the dialog loaded: show them and ask for the confirmation.
      if (isAlreadyOwned(e)) {
        setCoOwnerConfirmed(false);
        void owners.refetch();
      }
    },
  });
  const submit = () => {
    setFormError("");
    try {
      buildApproveBody(application.uid, mode === "existing" ? { mode, artistId } : { mode, displayName, bio });
    } catch (e) {
      setFormError(errorText(e));
      return;
    }
    if (needsCoOwnerConfirm && !coOwnerConfirmed) {
      setFormError("此藝人已有其他擁有者，請先勾選「我確認要加為共同藝人」");
      return;
    }
    approve.mutate();
  };
  return (
    <Dialog open onOpenChange={(open) => !open && !approve.isPending && onClose()}>
      <DialogContent className="max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>核准「{application.displayName}」</DialogTitle>
          <DialogDescription>選擇一種方式開通：綁定既有藝人專頁，或用申請資料建立新藝人。核准後申請者會成為該藝人的「藝人」成員。</DialogDescription>
        </DialogHeader>
        <div className="flex gap-2" role="radiogroup" aria-label="開通方式">
          {(["existing", "new"] as const).map((m) => (
            <Button key={m} size="sm" role="radio" aria-checked={mode === m} variant={mode === m ? "default" : "outline"} onClick={() => setMode(m)}>
              {m === "existing" ? "綁既有藝人" : "建立新藝人"}
            </Button>
          ))}
        </div>
        {mode === "existing" ? (
          <div className="space-y-2">
            <Label htmlFor="creator-approve-search">搜尋藝人（名稱或 ID）</Label>
            <Input id="creator-approve-search" value={search} placeholder={application.displayName} onChange={(e) => setSearch(e.target.value)} />
            {actors.isPending && <p className="text-sm">讀取藝人…</p>}
            {actors.isError && <p className="text-sm text-destructive">{errorText(actors.error)}</p>}
            {actors.isSuccess && matches.length === 0 && <p className="text-sm text-muted-foreground">找不到符合的藝人，可改用「建立新藝人」。</p>}
            <ul className="max-h-60 divide-y overflow-y-auto rounded border" role="listbox" aria-label="藝人">
              {matches.map((actor) => (
                <li key={actor.id}>
                  <button
                    type="button"
                    role="option"
                    aria-selected={actor.id === artistId}
                    className={`flex w-full items-center gap-2 px-2 py-2 text-left text-sm hover:bg-muted ${actor.id === artistId ? "bg-muted font-semibold" : ""}`}
                    onClick={() => {
                      setArtistId(actor.id);
                      setCoOwnerConfirmed(false);
                    }}
                  >
                    <ActorAvatar src={actor.photoURL ? getActorPhotoUrl(actor.photoURL) : ""} name={actor.name} size="h-8 w-8" />
                    <span className="min-w-0 flex-1 break-words">{actor.name || "（未命名）"}</span>
                    <span className="font-mono text-xs text-muted-foreground break-all">{actor.id}</span>
                  </button>
                </li>
              ))}
            </ul>
            <p className="text-sm">
              <span className="inline-flex items-center gap-2 align-middle">
                {selected && <ActorAvatar src={profile.data?.avatarUrl ?? (selected.photoURL ? getActorPhotoUrl(selected.photoURL) : "")} name={selected.name} size="h-8 w-8" />}
                <span>已選：{selected ? `${selected.name}（${selected.id}）` : "尚未選擇"}</span>
              </span>
            </p>
            {artistId &&
              (owners.isPending ? (
                <p className="text-sm text-muted-foreground">讀取目前擁有者…</p>
              ) : owners.isError ? (
                <p className="text-sm text-muted-foreground">無法讀取目前擁有者（{errorText(owners.error)}）；送出時後端仍會檢查。</p>
              ) : (
                <p className="text-sm break-words">目前擁有者：{owners.data.owners.length ? ownersLabel(owners.data.owners) : "無"}</p>
              ))}
            {needsCoOwnerConfirm && (
              <div className="space-y-2 rounded border border-destructive/50 p-2 text-sm">
                <p>此藝人已有其他擁有者。核准後申請者會成為「共同藝人」，與現有擁有者有相同的發布與設定權限。</p>
                <label className="flex items-center gap-2">
                  <input type="checkbox" checked={coOwnerConfirmed} onChange={(e) => setCoOwnerConfirmed(e.target.checked)} />
                  我確認要加為共同藝人
                </label>
              </div>
            )}
          </div>
        ) : (
          <div className="space-y-3">
            <div>
              <Label htmlFor="creator-approve-name">藝人名稱（≤{NEW_ARTIST_NAME_MAX} 字）</Label>
              <Input id="creator-approve-name" value={displayName} onChange={(e) => setDisplayName(e.target.value)} />
            </div>
            <div>
              <Label htmlFor="creator-approve-bio">簡介（≤{NEW_ARTIST_BIO_MAX} 字）</Label>
              <Textarea id="creator-approve-bio" rows={4} value={bio} onChange={(e) => setBio(e.target.value)} />
              <p className="text-xs text-muted-foreground">
                {[...bio.trim()].length}／{NEW_ARTIST_BIO_MAX}
              </p>
            </div>
          </div>
        )}
        {(formError || approve.isError) && (
          <p role="alert" className="text-sm text-destructive">
            {formError || errorText(approve.error)}
          </p>
        )}
        <DialogFooter>
          <Button variant="outline" disabled={approve.isPending} onClick={onClose}>
            取消
          </Button>
          <Button
            disabled={
              approve.isPending ||
              isStateConflict(approve.error) ||
              (mode === "existing" && !!artistId && owners.isPending) ||
              (needsCoOwnerConfirm && !coOwnerConfirmed)
            }
            onClick={submit}
          >
            {approve.isPending ? "開通中…" : "確認核准"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function RejectDialog({ application, onClose, onDone }: { application: CreatorApplication; onClose: () => void; onDone: () => void }) {
  const refresh = useApplicationMutationDone();
  const [reason, setReason] = useState("");
  const [touched, setTouched] = useState(false);
  const problem = rejectReasonProblem(reason);
  const reject = useMutation({
    mutationFn: () => creatorApplicationReject(application.uid, reason.trim()),
    onSuccess: () => {
      refresh();
      onDone();
    },
    onError: (e) => {
      if (isStateConflict(e)) refresh();
    },
  });
  return (
    <Dialog open onOpenChange={(open) => !open && !reject.isPending && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>拒絕「{application.displayName}」</DialogTitle>
          <DialogDescription>原因會顯示給申請者，請具體說明可以如何補件。</DialogDescription>
        </DialogHeader>
        <div className="flex flex-wrap gap-2" role="group" aria-label="常用原因">
          {CREATOR_REJECT_PRESETS.map((p) => (
            <Button
              key={p.label}
              size="sm"
              variant={reason === p.text ? "default" : "outline"}
              onClick={() => {
                setReason(p.text);
                setTouched(true);
              }}
            >
              {p.label}
            </Button>
          ))}
        </div>
        <div>
          <Label htmlFor="creator-reject-reason">拒絕原因（必填，至少 5 字）</Label>
          <Textarea
            id="creator-reject-reason"
            rows={4}
            maxLength={REJECT_REASON_MAX * 2}
            value={reason}
            aria-invalid={touched && !!problem}
            onChange={(e) => setReason(e.target.value)}
            onBlur={() => setTouched(true)}
          />
          {touched && problem && <p className="text-sm text-destructive">{problem}</p>}
        </div>
        {reject.isError && (
          <p role="alert" className="text-sm text-destructive">
            {errorText(reject.error)}
          </p>
        )}
        <DialogFooter>
          <Button variant="outline" disabled={reject.isPending} onClick={onClose}>
            取消
          </Button>
          <Button
            variant="destructive"
            disabled={reject.isPending || !!problem || isStateConflict(reject.error)}
            onClick={() => reject.mutate()}
          >
            {reject.isPending ? "送出中…" : "確認拒絕"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/** Avatar with a graceful fallback: broken or unreadable images (e.g. non-public storage) show the first letter instead. */
function ActorAvatar({ src, name, size }: { src: string; name: string; size: string }) {
  const [broken, setBroken] = useState(false);
  const initial = [...(name || "?")][0];
  if (!src || broken) {
    return (
      <span aria-hidden className={`flex ${size} shrink-0 items-center justify-center rounded-full bg-muted text-xs`}>
        {initial}
      </span>
    );
  }
  return <img src={src} alt="" className={`${size} shrink-0 rounded-full object-cover`} onError={() => setBroken(true)} />;
}
